import { useEffect, useRef } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Typography from '@mui/material/Typography'
import type { BellNotificationItem } from '../api/notifications-queries'
import {
  NOTIFICATIONS_END_OF_LIST,
  NOTIFICATIONS_LOAD_MORE_ERROR,
  NOTIFICATIONS_RETRY,
} from '../lib/notifications-copy'
import { notificationsListSx } from '../styles/notificationsPageStyles'
import { NotificationFullCard } from './NotificationFullCard'

type Props = {
  items: BellNotificationItem[]
  hasMore: boolean
  isFetchingNextPage: boolean
  isFetchNextPageError?: boolean
  onLoadMore: () => void
  onNavigate: (url: string) => void
}

export function NotificationFullList({
  items,
  hasMore,
  isFetchingNextPage,
  isFetchNextPageError = false,
  onLoadMore,
  onNavigate,
}: Readonly<Props>) {
  const sentinelRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const node = sentinelRef.current
    if (!node || !hasMore || isFetchNextPageError) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && !isFetchingNextPage) {
          onLoadMore()
        }
      },
      { rootMargin: '200px', threshold: 0.1 },
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [hasMore, isFetchingNextPage, isFetchNextPageError, onLoadMore])

  if (items.length === 0) {
    return (
      <Box sx={{ py: 6, textAlign: 'center' }} data-testid="provider-notifications-empty">
        <Typography sx={{ fontSize: 14, color: '#64748b' }}>No notifications in this tab.</Typography>
      </Box>
    )
  }

  return (
    <Box sx={notificationsListSx} data-testid="provider-notifications-list">
      {items.map((item) => (
        <NotificationFullCard key={item.id} notification={item} onNavigate={onNavigate} />
      ))}
      <Box ref={sentinelRef} sx={{ height: 1 }} aria-hidden />
      {isFetchingNextPage ? (
        <Box
          sx={{ display: 'flex', justifyContent: 'center', py: 3 }}
          data-testid="provider-notifications-loading-more"
          aria-busy
          aria-label="Loading more notifications"
        >
          <CircularProgress size={24} sx={{ color: '#64748b' }} />
        </Box>
      ) : null}
      {isFetchNextPageError ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1,
            py: 3,
            px: 2,
          }}
          data-testid="provider-notifications-load-more-error"
        >
          <Typography sx={{ fontSize: 14, color: '#64748b', textAlign: 'center' }}>
            {NOTIFICATIONS_LOAD_MORE_ERROR}
          </Typography>
          <Button size="small" variant="outlined" onClick={onLoadMore}>
            {NOTIFICATIONS_RETRY}
          </Button>
        </Box>
      ) : null}
      {!hasMore && items.length > 0 && !isFetchingNextPage ? (
        <Typography
          sx={{ textAlign: 'center', py: 2, fontSize: 14, color: '#94a3b8' }}
          data-testid="provider-notifications-end"
        >
          {NOTIFICATIONS_END_OF_LIST}
        </Typography>
      ) : null}
    </Box>
  )
}
